import { useParams, Link, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import ReactMarkdown from 'react-markdown'

const postModules = import.meta.glob('../content/blog/*.json', { eager: true })
const posts = Object.fromEntries(Object.values(postModules).map((p) => [p.slug, p]))

const BlogPostPage = () => {
  const { slug } = useParams()
  const post = posts[slug]

  if (!post) return <Navigate to="/blog" replace />

  const formattedDate = new Date(post.publishDate).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  // Cross-link to other guides, ranked by shared tags then recency, so every
  // post feeds internal links to its siblings and the money pages below.
  const relatedPosts = Object.values(posts)
    .filter((p) => p.slug !== post.slug)
    .map((p) => ({
      p,
      shared: (p.tags || []).filter((t) => (post.tags || []).includes(t)).length,
    }))
    .sort((a, b) => b.shared - a.shared || new Date(b.p.publishDate) - new Date(a.p.publishDate))
    .slice(0, 3)
    .map((x) => x.p)

  return (
    <>
      <Helmet>
        <title>{post.title} | Keyzee</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={`https://keyzee.co.uk/blog/${post.slug}/`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://keyzee.co.uk/blog/${post.slug}/`} />
        <meta property="og:site_name" content="Keyzee" />
        <meta property="og:image" content="https://keyzee.co.uk/og-image.jpg" />
        <meta property="article:published_time" content={post.publishDate} />
        {post.tags?.map((tag) => <meta key={tag} property="article:tag" content={tag} />)}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.metaDescription} />
        <meta name="twitter:image" content="https://keyzee.co.uk/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.metaDescription,
          datePublished: post.publishDate,
          url: `https://keyzee.co.uk/blog/${post.slug}/`,
          publisher: {
            '@type': 'Organization',
            name: 'Keyzee',
            url: 'https://keyzee.co.uk',
          },
        })}</script>
      </Helmet>

      <article className="blog-post">
        <header className="blog-post__header">
          <div className="blog-shell">
            <Link to="/blog/" className="blog-post__back">← All guides</Link>
            <p className="search-hero__eyebrow">Guide</p>
            <h1>{post.title}</h1>
            <div className="blog-post__meta">
              <time dateTime={post.publishDate}>{formattedDate}</time>
              <span>{post.readingTime} min read</span>
              {post.tags?.length > 0 && (
                <ul className="blog-post__tags" aria-label="Tags">
                  {post.tags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </header>

        <div className="blog-post__body blog-shell">
          <ReactMarkdown>{post.body}</ReactMarkdown>
        </div>

        <footer className="blog-post__footer blog-shell">
          <div className="blog-post__cta">
            <h2>Ready to list your property?</h2>
            <p>Free forever. No agent fees. Buyers contact you directly.</p>
            <Link to="/sell/new" className="primary-button">
              Start your listing →
            </Link>
          </div>

          <nav className="blog-post__related" aria-label="Related guides and tools">
            <h2>Guides &amp; tools</h2>
            <ul className="blog-post__links">
              <li><Link to="/sell-without-an-agent/">Sell without an estate agent</Link></li>
              <li><Link to="/house-valuation/">Free instant house valuation</Link></li>
              <li><Link to="/house-prices/">UK house prices by area</Link></li>
              <li><Link to="/listings">Browse homes for sale</Link></li>
            </ul>
            {relatedPosts.length > 0 && (
              <>
                <h3>More guides</h3>
                <ul className="blog-post__links">
                  {relatedPosts.map((p) => (
                    <li key={p.slug}><Link to={`/blog/${p.slug}/`}>{p.title}</Link></li>
                  ))}
                </ul>
              </>
            )}
          </nav>
        </footer>
      </article>
    </>
  )
}

export default BlogPostPage
